<template>
  <div id="living-images-viewer">
    <!-- back button -->
    <b-button
      id="back-button"
      class="fab"
      :to="{ name: 'details', query: filter, params: { locale: $route.params.locale, listType, id } }"
    >
      <icon-base
        :title="$t('livingImagesViewer.ui.backButtonTitle')"
        color="#ddd"
        role="img"
        height="28"
      >
        <icon-arrow-left />
      </icon-base>
    </b-button>
    <div id="info-buttons-wrapper">
      <!-- info button (opens info modal) -->
      <b-button
        class="info-button fab"
        v-b-modal.info-modal
      >
        <icon-base
          title="Info"
          width="28"
          height="28"
          color="#ddd"
          role="img"
        >
          <icon-info />
        </icon-base>
      </b-button>

      <!-- overview button (opens overview modal) -->
      <b-button
        class="info-button fab position-relative"
        v-b-modal.overview-modal
      >
        <icon-base
          :title="$t('livingImagesViewer.overviewModal.title')"
          width="28"
          height="28"
          color="#ddd"
          role="img"
        >
          <icon-living-images />
        </icon-base>
        <div v-if="unfoundLivingImages > 0" id="unfoundIndicator">
          {{ unfoundLivingImages }}
        </div>
      </b-button>
    </div>

    <!-- info modal -->
    <info-modal :version="version" :filter="filter" />

    <!-- overview modal -->
    <overview-modal :living-images="livingImages" />

    <!-- loading screen -->
    <transition name="fade">
      <div class="loading-screen" v-if="!cameraLoaded">
        <b-spinner id="loading-spinner" />
        <br>
        <span id="loading-subheader">
          {{ $t('livingImagesViewer.loadingText') }}
        </span>
      </div>
    </transition>

    <!-- A-Frame scene -->
    <a-scene
      id="scene"
      ref="scene"
      v-if="arjsLoaded"
      embedded
      :arjs="`debugUIEnabled: false;
        patternRatio: ${activeLivingImage ? activeLivingImage.patternRatio : 0.9}`"
      vr-mode-ui="enabled: false"
      cursor="fuse: false; rayOrigin: mouse"
      raycaster="objects: .clickable"
    >
      <!-- assets to be used -->
      <a-assets timeout="10000" @loaded="onAssetsLoaded" @timeout="onAssetsTimeout">
        <video
          :id="`v-${livingImage.id}`"
          :src="livingImage.video"
          webkit-playsinline
          playsinline
          crossorigin="anonymous"
          :muted="muted"
          v-for="livingImage in livingImages"
          :key="livingImage.id"
        >
          {{ $t('livingImagesViewer.videoElementNotSupported') }}
        </video>
      </a-assets>

      <!-- markers -->
      <a-marker
        :id="`m-${livingImage.id}`"
        emitevents="true"
        preset="custom"
        type="pattern"
        :url="livingImage.pattern"
        v-for="livingImage in livingImages"
        :key="livingImage.id"
        smooth="false"
      >
        <!-- video objects -->
        <a-video
          :id="`a-${livingImage.id}`"
          :class="livingImage.data.active ? 'clickable' : ''"
          :src="'#v-' + livingImage.id"
          position="0 0 0"
          rotation="270 0 0"
          height="0.9"
          width="0.9"
          clickable
          :living-image="`title: ${livingImage.title}; fadeTime: ${fadeTime}; loadingOverlay: #l-${livingImage.id}; muted: ${muted};`"
          material="opacity: 0"
          :animation__fade-in="`property: components.material.material.opacity;
            from: 0;
            to: 1;
            dur: ${fadeTime};
            startEvents: fade-in`"
          :animation__fade-out="`property: components.material.material.opacity;
            from: 1;
            to: 0;
            dur: ${fadeTime};
            startEvents: fade-out`"
        />

        <!-- loading overlay -->
        <a-plane
          :id="'l-' + livingImage.id"
          src="/img/living-images/loading_overlay_smaller.png"
          position="0 0.03 0"
          width="0.6"
          height="0.6"
          material="transparent: true"
          :animation__rotation="`property: rotation;
            from: 270 0 0;
            to: 270 0 360;
            dir: reverse;
            loop: true;
            dur: 700;
            easing: linear;
            autoplay: true`"
        />
      </a-marker>

      <!-- virtual camera -->
      <a-entity id="camera" camera />
    </a-scene>

    <!-- bottom button bar -->
    <div id="bottom-bar">
      <!-- console messages -->
      <!-- <div id="console">
        <div id="console-content" ref="console-content">
          <span v-for="(msg, key) in consoleMessages" :key="key">{{ msg }}<br></span>
        </div>
        <button @click="consoleMessages = []">Leeren</button>
      </div> -->

      <!-- play/pause button -->
      <b-button
        @click="onPlayBtn"
        :disabled="!activeLivingImage
          || activeLivingImage.data.loading
          || activeLivingImage.data.ended
          || activeLivingImage.data.fading"
        id="play-button"
        class="bottom-bar-btn fab"
      >
        <img
          src="@/assets/images/living-images/icons/pause.svg"
          alt="Pause"
          width="36"
          height="36"
          v-if="activeLivingImage && activeLivingImage.data.playing"
        >
        <img
          src="@/assets/images/living-images/icons/play_arrow.svg"
          alt="Play"
          width="36"
          height="36"
          v-else
        >
      </b-button>

      <!-- mute/unmute button -->
      <b-button
        @click="onMuteBtn"
        id="mute-button"
        class="bottom-bar-btn fab"
        :class="{'muted': muted, 'pulse': !muteBtnPressed}"
      >
        <img
          src="@/assets/images/living-images/icons/volume_off.svg"
          alt="Muted"
          width="20"
          height="20"
          v-if="muted"
        >
        <img
          src="@/assets/images/living-images/icons/volume_up.svg"
          alt="Unmuted"
          width="20"
          height="20"
          v-else
        >
      </b-button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import InfoModal from '@/components/living-images/InfoModal.vue'
import OverviewModal from '@/components/living-images/OverviewModal.vue'
import detectRTC from 'detectrtc'
import 'aframe'
import '@/components/aframe/clickable'
import '@/components/aframe/living-image'

export default {
  name: 'LivingImagesViewer',
  components: {
    InfoModal,
    OverviewModal
  },
  data() {
    return {
      version: '0.1.0',
      arjsLoaded: false,
      cameraLoaded: false,
      livingImages: [],
      muted: true,
      muteBtnPressed: false,
      fadeTime: 1500, // milliseconds
      consoleMessages: []
    }
  },
  props: {
    id: {
      type: String,
      default: 'act0002156'
    },
    listType: {
      type: String,
      default: 'list'
    }
  },
  computed: {
    ...mapGetters({
      filter: 'filters/getFilterQuery'
    }),
    activeLivingImage(oldValue) {
      if (this.livingImages.length === 0 || !this.livingImages[0].data) return
      return this.livingImages.find(livingImage => {
        return livingImage.data.active
      })
    },
    unfoundLivingImages() {
      return this.livingImages.filter(li => !li.data.found).length
    }
  },
  watch: {
    consoleMessages() {
      /* setTimeout(() => {
        this.$refs['console-content'].scrollTop = this.$refs['console-content'].scrollHeight
      }, 50) */
    }
  },
  methods: {
    onAssetsLoaded() {
      console.log('Assets loaded')
      this.consoleMessages.push('Assets loaded')

      // add additional properties and event listeners to living images here
      for (let livingImage of this.livingImages) {
        // assign elements to living image objects
        livingImage.element = this.getElement('a', livingImage.id)
        livingImage.videoElement = this.getElement('v', livingImage.id)
        livingImage.markerElement = this.getElement('m', livingImage.id)
        livingImage.loadingElement = this.getElement('l', livingImage.id)

        // sync vue object data with a-frame component data by setting reference
        livingImage.element.addEventListener('loaded', e => {
          console.log('LI Component loaded')
          livingImage.data = e.target.components['living-image'].data
        })

        // listen for console logs
        livingImage.element.addEventListener('consolemessage', msg => {
          console.log(msg.detail)
          this.consoleMessages.push(msg.detail)
        })
      }
    },
    onAssetsTimeout(err) {
      console.error('Zeitüberschreitung beim Laden der Assets', err.message)
      this.consoleMessages.push('Zeitüberschreitung beim Laden der Assets' + err.message)
    },
    // called on play button click
    onPlayBtn() {
      if (!this.activeLivingImage) return
      this.activeLivingImage.element.setAttribute('living-image', 'userPaused', !this.activeLivingImage.data.userPaused)
    },
    // called on mute button click
    onMuteBtn() {
      if (!this.muteBtnPressed) this.muteBtnPressed = true
      this.muted = !this.muted
    },
    getElement(prefix, id) {
      // prefixes: m = marker, v = video, a = living-image, l = loading overlay
      return document.getElementById(`${prefix}-${id}`)
    }
  },
  async created() {
    if (!detectRTC.isGetUserMediaSupported) {
      this.$router.push(`/${this.$route.params.locale}/institutions/${this.listType}/details/${this.id}#living-images-info`)
    }
    const institution = await this.$store.dispatch('institutions/fetchDetails', { id: this.id, locale: this.$i18n.locale })
    this.livingImages = institution.livingImages.map(livingImage => {
      return {
        ...livingImage,
        data: {},
        element: null,
        videoElement: null,
        markerElement: null,
        loadingElement: null
      }
    })

    // set scale to 1.0 to avoid scaling problems with camera
    document.getElementById('meta-viewport').content = 'width=device-width, user-scalable=no, minimum-scale=1.0, maximum-scale=1.0'

    // attach arjs script to document
    let arjsScript = document.createElement('script')
    arjsScript.id = 'arjs-script'
    arjsScript.onload = () => {
      this.arjsLoaded = true
    }
    arjsScript.setAttribute('src', '/scripts/aframe-ar.js') // regularly look for updates!
    document.head.appendChild(arjsScript)

    // hide loading screen when camera loaded
    window.addEventListener('arjs-video-loaded', () => {
      this.cameraLoaded = true
    })
  }
}
</script>

<style lang="scss">
body, #app {
  background: none !important;
  overflow: hidden !important;
  height: 100%;
}
.loading-screen {
  position: fixed;
  top: 0;
  left: 0;
  background-color: #fff;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-flow: column;
  z-index: 100;
}
#loading-spinner {
  color: $primary;
  width: 50px;
  height: 50px;
}
#loading-subheader {
  color: $primary;
  font-size: 1em;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity .3s ease;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

#ar-wrapper {
  height: 100%;
  position: relative;
}
#living-images-viewer .btn {
  background-color: $primary;
  color: #fff;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 0;
}
#living-images-viewer .btn:hover, .btn:focus, .btn.focus {
  box-shadow: none;
}
#living-images-viewer .btn:active:not(.disabled) {
  box-shadow: 0px 0px 11px 1px rgba(255, 255, 255, 0.5) !important;
}
#living-images-viewer .btn.disabled {
  background-color: rgb(46, 49, 49);
}
#back-button {
  top: 15px;
  left: 15px;
  height: 42px;
  width: 42px;
  border-radius: 21px;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 101;
  position: fixed;
}
#info-buttons-wrapper {
  bottom: 20%;
  right: 15px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-flow: column wrap;
  z-index: 11;
  position: fixed;
}
.info-button {
  height: 42px;
  width: 42px;
  border-radius: 21px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
  margin-top: 16px;
}
#unfoundIndicator {
  height: 20px;
  width: 20px;
  background-color: #fff;
  color: $primary;
  font-size: 0.6rem;
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  left: 29px;
  bottom: 29px;
  padding: 0.1rem;
  border-radius: 50%;
}
#play-button {
  width: 60px;
  height: 60px;
  border-radius: 30px;
}
#mute-button {
  width: 46px;
  height: 46px;
  border-radius: 23px;
}
#mute-button * {
  pointer-events: none;
}
.muted {
  background-color: $red !important;
}
.pulse {
  animation: pulse 1s infinite;
}
@keyframes pulse {
  50% {
     transform:scale(1.3);
  }
}
#bottom-bar {
  position: fixed;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  display:flex;
  align-items: center;
  justify-content: center;
}
.bottom-bar-btn {
  margin: 0 10px;
}
#console {
  width: 80vw;
  background-color: rgba(0, 48, 100, 0.7);
  padding: 8px;
  margin-bottom: 10px;
}
#console-content {
  color: #fff;
  height: 20vh;
  overflow-y: auto;
  border-bottom: 1px solid #ccc;
  margin-bottom: 8px;
  padding-bottom: 5px;
}
/* .video-thumbnail {
  position: relative;
  display: inline-block;
  cursor: pointer;
  margin: 30px;
}
.video-thumbnail:before {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  -webkit-transform: translate(-50%, -50%);
  content: "\f01d";
  font-size: 100px;
  color: #fff;
  opacity: 0.8;
  text-shadow: 0px 0px 30px rgba(0, 0, 0, 0.5);
}
.video-thumbnail:hover:before {
  color: #eee;
} */
#ar-viewer button:focus {
  box-shadow: none !important;
}
</style>
